import React from 'react';
import { isPast } from 'date-fns';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import Router from 'next/router';
import { twMerge } from 'tailwind-merge';
import { gql } from '@soundxyz/gql-string';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { Background } from '@components/Song/Hero/Background';
import { Countdown } from '@components/Song/Hero/Countdown';
import { SongCardDropdown } from '@components/SongCards/shared/SongCardDropdown';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { FeedReleaseCardFragmentDoc } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';
import { CoverImage } from './CoverImage';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedReleaseCard on Release {
    id
    title
    mintStartTimestamp
    webappUri
    type
    listeningParty
    artist {
      id
      name
      webappUri
      userId
    }
    track {
      id
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    animatedCoverImage {
      id
      url
    }
    ...SongCardDropdownInfo
  }
`);

export const FeedReleaseCard = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<FeedReleaseCardFragmentDoc>;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const [, forceUpdate] = React.useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const release = getFragment(FeedReleaseCardFragmentDoc, releaseFrag);
  const {
    coverImage,
    animatedCoverImage,
    staticCoverImage,
    track,
    title,
    artist,
    mintStartTimestamp,
    listeningParty,
  } = release;

  const {
    onPlayClick,
    isLoading,
    ref: playButtonPrefetchRef,
    currentListeningParty,
  } = usePlayButton({
    isAlbum: release.type === 'ALBUM',
    trackId: release.track.id,
    listeningParty: release.listeningParty,
    releaseId: release.id,
  });

  const hideCountdown =
    isPast(mintStartTimestamp) || (listeningParty != null && !currentListeningParty);

  const isAudioEnabled = release.type !== 'ALBUM';

  const variants: Variants = {
    initial: {
      opacity: tablet ? 1 : 0,
      y: 0,
    },

    animate: {
      opacity: 1,
      y: tablet ? (hideCountdown ? 60 : 0) : 0,
    },
  };

  const releaseLink = webappUriPath(release.webappUri);

  const { playing, activeTrackId } = useAudioController();

  const isPlaying = track.id === activeTrackId && playing;

  React.useEffect(() => {
    //prefetch release page
    Router.prefetch(releaseLink);
  }, [releaseLink]);

  return (
    <div
      className="relative flex aspect-square w-full min-w-0 flex-row-reverse items-center justify-center overflow-hidden rounded-md border-none outline-none hover:cursor-pointer min-[400px]:h-[343px] min-[400px]:justify-start md:aspect-[unset]"
      onClick={() => {
        Router.push(releaseLink);
      }}
    >
      {tablet && <Background coverImage={staticCoverImage?.url || coverImage.url} rounded />}
      {coverImage.url && (
        <div className="relative flex h-full w-full flex-shrink-0 overflow-hidden md:aspect-square md:w-[unset] md:justify-end md:p-5">
          <div className="three-dots-menu absolute right-3 top-3 z-above1 md:right-6 md:top-6">
            <SongCardDropdown
              triggerStyles={{
                transition: 'background 0.2s',
                '&:hover': { background: '$neutral300' },
                svg: {
                  path: {
                    fill: '$white',
                  },
                },
              }}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              release={release}
            />
          </div>
          <CoverImage
            releaseTitle={title}
            isAnimated={!!animatedCoverImage}
            coverImage={animatedCoverImage?.url || coverImage.url}
            trackDisabled={!isAudioEnabled}
            hasCountdown={!hideCountdown}
            webappUri={release.webappUri}
          />
        </div>
      )}
      <motion.div
        className={twMerge(
          'absolute flex h-full w-full flex-col items-center justify-center gap-6 overflow-hidden md:relative md:items-start md:justify-between md:p-[30px] ',
          hideCountdown && 'justify-end pb-5 md:justify-start md:pb-[30px]',
        )}
        variants={variants}
        initial="initial"
        animate="animate"
        transition={{
          ease: 'easeInOut',
        }}
      >
        <div
          className={twMerge(
            'flex flex-col items-center scroll-smooth md:items-start',
            hideCountdown &&
              'h-[58%] w-full min-w-0 flex-col items-center justify-between md:flex-row md:items-center md:gap-3',
          )}
        >
          {hideCountdown && (
            <PlayButton
              color="white"
              playing={isPlaying}
              onClick={e => {
                e.stopPropagation();
                onPlayClick();
              }}
              loading={isLoading}
              disabled={!isAudioEnabled}
              size="l"
              ref={playButtonPrefetchRef}
              currentListeningParty={currentListeningParty}
            />
          )}

          <div
            className={twMerge(
              'flex w-full flex-col items-center justify-center gap-1 overflow-hidden md:mt-0.5 md:items-start',
              hideCountdown && 'items-start px-2.5',
            )}
          >
            <AccessibleLink
              className="max-w-full"
              pathnameUri={webappUriPath(artist.webappUri)}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <h3 className="artistName font-base text-base-s font-semibold text-white">
                {artist.name}
              </h3>
            </AccessibleLink>
            {!!title && (
              <AccessibleLink
                className={twMerge('max-w-full ', hideCountdown ? 'px-0' : 'px-2 md:px-0')}
                pathnameUri={webappUriPath(release.webappUri)}
              >
                <h3
                  className={twMerge(
                    'line-clamp-2 overflow-hidden text-ellipsis whitespace-pre-line font-title !text-title-l font-medium text-white md:justify-start md:text-left dt:line-clamp-4',
                    hideCountdown ? 'justify-start text-left' : 'justify-center text-center',
                  )}
                >
                  {title}
                </h3>
              </AccessibleLink>
            )}
          </div>
        </div>
        {!hideCountdown && (
          <div className="flex flex-col items-center scroll-smooth md:items-start">
            <div className="flex flex-col items-center justify-center md:items-start">
              <Countdown
                mintStartTimestamp={mintStartTimestamp}
                css={{
                  gap: 12,
                  '@tablet': {
                    alignItems: 'flex-start',
                  },
                }}
                onCountdownExpire={() => forceUpdate(v => ++v)}
                currentListeningParty={currentListeningParty}
                listeningParty={listeningParty}
              />
            </div>
            {!hideCountdown && (
              <GetNotifiedButton
                type="feed_release_card"
                artistId={artist.id}
                userId={artist.userId}
              />
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};
